import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { GitCommit, Plus, Minus, Edit, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export const CommitDialog = ({ isOpen, onClose, onCommit, pendingChanges }) => {
  const [commitMessage, setCommitMessage] = useState('');
  const [isCommitting, setIsCommitting] = useState(false);
  const [error, setError] = useState('');

  const handleCommit = async () => {
    // Validate commit message
    if (!commitMessage || commitMessage.trim().length < 10) {
      setError('Commit message must be at least 10 characters');
      return;
    }

    if (commitMessage.length > 500) {
      setError('Commit message must be less than 500 characters');
      return;
    }

    setIsCommitting(true);
    setError('');

    try {
      await onCommit(commitMessage.trim());
      setCommitMessage('');
      onClose();
      toast.success('Changes committed successfully');
    } catch (err) {
      setError(err.message || 'Failed to commit changes');
      toast.error('Failed to commit changes');
    } finally {
      setIsCommitting(false);
    }
  };

  const handleClose = () => {
    if (!isCommitting) {
      setCommitMessage('');
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCommit className="w-5 h-5 text-primary" />
            Commit Changes
          </DialogTitle>
          <DialogDescription>
            Create a new version with a commit message. This will save your changes permanently.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Commit Message Input */}
          <div className="space-y-2">
            <Label htmlFor="commit-message">
              Commit Message <span className="text-red-500">*</span>
            </Label>
            <Input
              id="commit-message"
              placeholder="e.g., Added email notification step"
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
              className={error ? 'border-red-500' : ''}
              disabled={isCommitting}
              autoFocus
            />
            {error && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {error}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {commitMessage.length}/500 characters (min: 10)
            </p>
          </div>

          {/* Pending Changes Summary */}
          {pendingChanges && (
            <div className="border rounded-lg p-4 bg-muted/30">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Edit className="w-4 h-4" />
                Pending Changes
              </h4>
              <div className="space-y-2">
                {pendingChanges.nodes_added > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <Badge variant="success" className="bg-green-100 text-green-700">
                      <Plus className="w-3 h-3 mr-1" />
                      {pendingChanges.nodes_added} node(s) added
                    </Badge>
                  </div>
                )}
                {pendingChanges.nodes_removed > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <Badge variant="destructive" className="bg-red-100 text-red-700">
                      <Minus className="w-3 h-3 mr-1" />
                      {pendingChanges.nodes_removed} node(s) removed
                    </Badge>
                  </div>
                )}
                {pendingChanges.nodes_modified > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <Badge variant="warning" className="bg-orange-100 text-orange-700">
                      <Edit className="w-3 h-3 mr-1" />
                      {pendingChanges.nodes_modified} node(s) modified
                    </Badge>
                  </div>
                )}
                {pendingChanges.edges_added > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <Badge variant="success" className="bg-green-100 text-green-700">
                      <Plus className="w-3 h-3 mr-1" />
                      {pendingChanges.edges_added} connection(s) added
                    </Badge>
                  </div>
                )}
                {pendingChanges.edges_removed > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <Badge variant="destructive" className="bg-red-100 text-red-700">
                      <Minus className="w-3 h-3 mr-1" />
                      {pendingChanges.edges_removed} connection(s) removed
                    </Badge>
                  </div>
                )}
                {!pendingChanges.nodes_added && !pendingChanges.nodes_removed && 
                 !pendingChanges.nodes_modified && !pendingChanges.edges_added && 
                 !pendingChanges.edges_removed && (
                  <p className="text-sm text-muted-foreground">No changes detected</p>
                )}
              </div>
            </div>
          )}

          {/* Commit Message Suggestions */}
          <div className="border rounded-lg p-3 bg-blue-50">
            <p className="text-xs font-medium text-blue-900 mb-2">💡 Commit Message Tips:</p>
            <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
              <li>Start with a verb: "Added", "Fixed", "Updated", "Removed"</li>
              <li>Be specific: "Added email notification for order completion"</li>
              <li>Keep it concise but descriptive</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isCommitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCommit}
            disabled={isCommitting || !commitMessage || commitMessage.trim().length < 10}
            className="bg-primary"
          >
            {isCommitting ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Committing...
              </>
            ) : (
              <>
                <GitCommit className="w-4 h-4 mr-2" />
                Commit
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
